// ============================================================
//  src/pages/bots/tabs/CommandsTab.js
//  Updated: trigger types, draft/publish workflow, test sandbox
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import {
  listCommands, createCommand, updateCommand, deleteCommand,
  publishCommand, unpublishCommand, testCommand, toggleCommandActive,
} from '../../../api/client';
import toast from 'react-hot-toast';
import './tabs.css';

const TYPES = ['command', 'wildcard', 'webhook', 'config', 'flow'];
const LANGUAGES = ['javascript', 'python', 'json'];
const TRIGGER_TYPES = ['exact', 'contains', 'regex', 'wildcard'];

const TYPE_META = {
  command: { icon: '⌘', label: 'Command' },
  wildcard: { icon: '✶', label: 'Wildcard' },
  webhook: { icon: '⇄', label: 'Webhook' },
  config: { icon: '⚙', label: 'Config' },
  flow: { icon: '⌥', label: 'Flow' },
};

const LANG_META = {
  javascript: { label: 'JavaScript', dot: '#f0b400' },
  python: { label: 'Python', dot: '#3572A5' },
  json: { label: 'JSON', dot: '#8e8e93' },
};

const TRIGGER_TYPE_META = {
  exact: { label: 'Exact match', hint: 'Fires only when the message matches exactly.' },
  contains: { label: 'Contains', hint: 'Fires when the trigger text appears anywhere in the message.' },
  regex: { label: 'Regex', hint: 'e.g. order #(\\d+) — capture groups become {{match.1}}, {{match.2}}…' },
  wildcard: { label: 'Wildcard (*)', hint: 'Catch-all fallback when nothing else matches. Leave trigger as * or empty.' },
};

const DEFAULT_CODE = {
  javascript: `// Available globals: ctx, chat, storage, Panel, WebhookLib, options, fetch
// ctx.bot.sendMessage(chat.id, "Hello!");

module.exports = async () => {
  await ctx.bot.sendMessage(chat.id, "Hello from " + ctx.bot.name);
};`,
  python: `# Available globals: ctx, chat, storage, Panel
# ctx.bot.send_message(chat.id, "Hello!")

async def handler():
    await ctx.bot.send_message(chat.id, "Hello!")`,
  json: `[]`,
};

// ── CodeMirror 6 — lazy loader via npm packages ─────────────
//  These are regular npm imports — webpack bundles them normally.
//  We use a dynamic import() of a LOCAL helper module so the
//  ~200 kB CM6 bundle is code-split and only loaded on first
//  editor open, keeping the main bundle fast.
//
//  RUN ONCE in your project root:
//    npm install @codemirror/state @codemirror/view @codemirror/commands \
//      @codemirror/language @codemirror/autocomplete @codemirror/lint \
//      @codemirror/lang-json @codemirror/lang-javascript @codemirror/lang-python \
//      @codemirror/theme-one-dark
//
//  Then create  src/lib/codemirrorBundle.js  (provided separately).
let _cmPromise = null;
function loadCodeMirror() {
  if (_cmPromise) return _cmPromise;
  // Dynamic import of a LOCAL file — CRA/webpack splits this into a
  // separate chunk automatically. No external URL, no CSP issues.
  _cmPromise = import(/* webpackChunkName: "codemirror" */ '../../../lib/codemirrorBundle')
    .then(m => m.default || m)
    .catch(err => {
      _cmPromise = null; // allow retry on next open
      throw err;
    });
  return _cmPromise;
}
// ── CommandKit action snippets for JSON autocomplete ──────────
const ACTION_SNIPPETS = {
  send_text: '{"type":"send_text","text":"Hello {{name}}!"}',
  send_buttons: '{"type":"send_buttons","body":"Choose:","header":"","footer":"","buttons":[{"id":"opt1","title":"Option 1"},{"id":"opt2","title":"Option 2"}]}',
  send_list: '{"type":"send_list","body":"Pick from menu:","button":"View Options","sections":[{"title":"Section","rows":[{"id":"item1","title":"Item 1","description":""}]}]}',
  send_flow: '{"type":"send_flow","flow_id":"{{panel.flow_id}}","header":"Get started","body":"Fill in the form.","footer":"","button_label":"Open"}',
  send_template: '{"type":"send_template","name":"template_name","language":"en_US","components":[]}',
  send_reaction: '{"type":"send_reaction","emoji":"👋","message_id":"{{msg_id}}"}',
  send_raw: '{"type":"send_raw","payload":{}}',
  http_post: '{"type":"http_post","url":"https://api.example.com/endpoint","body":{"key":"{{value}}"},"headers":{"Authorization":"Bearer {{panel.token}}"},"save_as":"result"}',
  http_post_form: '{"type":"http_post_form","url":"https://api.example.com/form","body":{"field":"{{value}}"},"save_as":"result"}',
  set_storage: '{"type":"set_storage","key":"step","value":"{{message}}"}',
  conversation_wait: '{"type":"conversation_wait","command":"/mycommand","step":"awaiting_input"}',
  conversation_clear: '{"type":"conversation_clear"}',
  if_else: '{"type":"if_else","left":"{{message.is_media}}","op":"==","right":"1","then":[{"type":"http_post","url":"https://api.example.com/analyze","body":{"image_url":"{{message.media_url}}","mime":"{{message.mime_type}}"},"headers":{"Authorization":"Bearer {{panel.token}}"},"save_as":"result"},{"type":"send_text","text":"Got your {{message.type}} — checking it now…"}],"else":[{"type":"send_text","text":"Matched!"}]}',
  for_each: '{"type":"for_each","items":"{{result.data}}","actions":[{"type":"send_text","text":"Item: {{loop.item}}"}]}',
  route_flow: '{"type":"route_flow","save_as":"flow_data"}',
  log: '{"type":"log","level":"info","message":"Debug: {{message}}"}',
  wait: '{"type":"wait","seconds":1}',
};

function makeJsonActionCompletion(cm6) {
  const { CompletionContext } = cm6;
  return function jsonActionCompletions(context) {
    const word = context.matchBefore(/"([a-z_]*)$/);
    const typeVal = context.matchBefore(/"type"\s*:\s*"([a-z_]*)$/);
    if (!word && !typeVal) return null;
    if (typeVal) {
      return {
        from: context.pos - (typeVal[0].length - typeVal[0].indexOf('"', typeVal[0].lastIndexOf(':') + 1) - 1),
        options: Object.keys(ACTION_SNIPPETS).map(k => ({
          label: k,
          type: 'keyword',
          detail: 'action type',
          boost: 99,
        })),
      };
    }
    return null;
  };
}
// ── Icons (tiny inline SVGs, no extra deps) ─────────────────────
function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M4 7h16M9 7V4.8A.8.8 0 0 1 9.8 4h4.4a.8.8 0 0 1 .8.8V7m2 0-.8 12.2a1.6 1.6 0 0 1-1.6 1.5H8.4a1.6 1.6 0 0 1-1.6-1.5L6 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function WarnIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M12 3 2 21h20Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 9.5v5M12 17.2v.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
function PlayIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <path d="M6 4.5v15l13-7.5Z" fill="currentColor" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}
function RocketIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <path d="M12 2c2.5 2 4 5.5 4 9 0 1.5-.3 2.8-.8 4M12 2c-2.5 2-4 5.5-4 9 0 1.5.3 2.8.8 4M9.2 15l-3 3 .8 3 3-3M14.8 15l3 3-.8 3-3-3M12 11a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Quick preview modal — opened directly from the command list ──
//  Reuses TestSandbox as-is (same guards, same run logic, same chat/trace
//  toggle) so there's exactly one place that knows how to run and render
//  a test — this is just a lighter-weight entry point into it.
function QuickPreviewModal({ bId, cmd, onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-group">
            <span className="modal-title-icon">👁</span>
            <div>
              <div className="modal-title">Preview</div>
              <div className="modal-subtitle">"{cmd.cTrigger || '*'}" — what a user would actually see</div>
            </div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        <div className="modal-body">
          <TestSandbox bId={bId} cmd={cmd} language={cmd.cLanguage} />
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Command row ───────────────────────────────────────────────
function CommandRow({ cmd, onEdit, onDelete, onPreview, onPublish, onUnpublish, onToggleActive, publishing, togglingActive }) {
  const meta = TYPE_META[cmd.cType] ?? { icon: '•', label: cmd.cType };
  const lang = LANG_META[cmd.cLanguage] ?? { label: cmd.cLanguage, dot: '#999' };
  const triggerType = TRIGGER_TYPE_META[cmd.cTriggerType] ?? TRIGGER_TYPE_META.exact;
  const isLive = cmd.cStatus === 'live';
  const isActive = cmd.cIsActive == 1;

  return (
    <div className="cmd-row">
      <div className="cmd-row-left">
        <span className={`cmd-type-badge cmd-type-${cmd.cType}`}>
          <span className="cmd-type-icon">{meta.icon}</span>{meta.label}
        </span>
        <span className="cmd-trigger">{cmd.cTrigger || '*'}</span>
        <span className="cmd-trigger-type-badge" title={triggerType.hint}>{triggerType.label}</span>
        <span className="cmd-lang">
          <span className="lang-dot" style={{ background: lang.dot }} />
          {lang.label}
        </span>
      </div>
      <div className="cmd-row-right">
        {/* Active/Inactive inline toggle — updates DB immediately, no modal needed */}
        <button
          type="button"
          className={`toggle-field toggle-field-sm ${isActive ? 'is-on' : ''}`}
          onClick={() => onToggleActive(cmd, !isActive)}
          disabled={togglingActive}
          title={isActive ? 'Click to deactivate' : 'Click to activate'}
        >
          <span className="toggle-track"><span className="toggle-thumb" /></span>
          <span className="toggle-label-sm">{isActive ? 'Active' : 'Inactive'}</span>
        </button>

        <span className={`status-pill ${isLive ? 'active' : 'inactive'}`}>
          <span className="status-dot" />
          {isLive ? 'Live' : 'Draft'}
        </span>
        {isLive ? (
          <button className="btn btn-sm btn-outline" onClick={() => onUnpublish(cmd)} disabled={publishing}>
            Unpublish
          </button>
        ) : (
          <button className="btn btn-sm btn-primary" onClick={() => onPublish(cmd)} disabled={publishing}>
            <RocketIcon /> Publish
          </button>
        )}
        <button className="icon-btn" onClick={() => onPreview(cmd)} title="Preview what this sends" aria-label="Preview command">
          <EyeIcon />
        </button>
        <button className="icon-btn" onClick={() => onEdit(cmd)} title="Edit command" aria-label="Edit command">
          <PencilIcon />
        </button>
        <button className="icon-btn danger" onClick={() => onDelete(cmd)} title="Delete command" aria-label="Delete command">
          <TrashIcon />
        </button>
      </div>
    </div>
  );
}

// ── Mini code editor with VS Code-like chrome ───────────────────
// ── CodeMirror 6 editor ──────────────────────────────────────
//  Renders a real CM6 editor; falls back to a plain <textarea>
//  while the ~80 kB bundle loads (usually <1 s on first open).
function CodeEditor({ value, onChange, language }) {
  const hostRef = useRef(null);
  const viewRef = useRef(null);           // CM6 EditorView instance
  const onChangeRef = useRef(onChange);
  const [cmReady, setCmReady] = useState(false);
  const [cmError, setCmError] = useState(false);
  const [editorHeight, setEditorHeight] = useState(380);
  const [cursorInfo, setCursorInfo] = useState({ line: 1, col: 1, lines: 1 });

  // Keep onChange ref current so the CM6 listener always calls latest
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // ── Boot CM6 ────────────────────────────────────────────────
  useEffect(() => {
    if (!hostRef.current) return;
    let cancelled = false;
    let view = null;

    loadCodeMirror().then((cm) => {
      if (cancelled || !hostRef.current) return;

      const {
        EditorState, EditorView, keymap, lineNumbers,
        highlightActiveLineGutter, drawSelection, dropCursor,
        rectangularSelection, highlightActiveLine, highlightSpecialChars,
        defaultKeymap, history, historyKeymap, indentWithTab,
        defaultHighlightStyle, syntaxHighlighting, indentOnInput,
        bracketMatching, foldGutter, foldKeymap,
        autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap,
        lintKeymap, linter,
        json: jsonLang, jsonParseLinter,
        javascript: jsLang,
        python: pyLang,
        oneDark,
      } = cm;

      // Language + lint extension
      const langExt = language === 'json'
        ? [jsonLang(), linter(jsonParseLinter())]
        : language === 'python'
          ? [pyLang()]
          : [jsLang()];

      // JSON action-type autocomplete (only for json language)
      const acExt = language === 'json'
        ? [autocompletion({ override: [makeJsonActionCompletion(cm)] })]
        : [autocompletion()];

      // Update listener — push value back to React
      const updateListener = EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          onChangeRef.current(update.state.doc.toString());
        }
        // Cursor info
        const sel = update.state.selection.main;
        const line = update.state.doc.lineAt(sel.head);
        setCursorInfo({
          line: line.number,
          col: sel.head - line.from + 1,
          lines: update.state.doc.lines,
        });
      });

      // Resize-aware height
      const editorTheme = EditorView.theme({
        '&': { height: '100%', fontSize: '13px', fontFamily: "'SF Mono','Fira Mono','Consolas',monospace" },
        '.cm-scroller': { overflow: 'auto' },
        '.cm-content': { padding: '8px 0' },
      });

      const state = EditorState.create({
        doc: value,
        extensions: [
          lineNumbers(),
          highlightActiveLineGutter(),
          highlightSpecialChars(),
          history(),
          drawSelection(),
          dropCursor(),
          rectangularSelection(),
          highlightActiveLine(),
          indentOnInput(),
          syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
          bracketMatching(),
          closeBrackets(),
          ...langExt,
          ...acExt,
          keymap.of([
            ...closeBracketsKeymap,
            ...defaultKeymap,
            ...historyKeymap,
            ...foldKeymap,
            ...completionKeymap,
            ...lintKeymap,
            indentWithTab,
          ]),
          oneDark,
          editorTheme,
          updateListener,
        ],
      });

      view = new EditorView({ state, parent: hostRef.current });
      viewRef.current = view;
      setCmReady(true);
    }).catch((err) => {
      console.error('[CommandKit] CodeMirror load failed:', err);
      if (!cancelled) setCmError(true);
    });

    return () => {
      cancelled = true;
      if (view) { view.destroy(); viewRef.current = null; }
    };
    // Re-create editor when language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // ── Sync external value changes into CM6 ────────────────────
  //  (e.g. when user switches language → new DEFAULT_CODE)
  const lastValueRef = useRef(value);
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current !== value && value !== lastValueRef.current) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: value },
      });
    }
    lastValueRef.current = value;
  }, [value]);

  // ── Resize handle ────────────────────────────────────────────
  function onResizeMouseDown(e) {
    e.preventDefault();
    const startY = e.clientY, startH = editorHeight;
    const onMove = (mv) => setEditorHeight(Math.max(200, Math.min(800, startH + mv.clientY - startY)));
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  const filename = `handler.${language === 'javascript' ? 'js' : language === 'python' ? 'py' : 'json'}`;

  return (
    <div className="vsc-editor">
      {/* Title bar */}
      <div className="vsc-titlebar">
        <span className="vsc-tab">
          <span className="vsc-tab-dot" />
          {filename}
        </span>
        <span className="vsc-titlebar-spacer" />
        {!cmReady && !cmError && (
          <span className="vsc-loading-badge">Loading editor…</span>
        )}
        {cmError && (
          <span className="vsc-error-badge" title="Run: npm install @codemirror/state @codemirror/view @codemirror/commands @codemirror/language @codemirror/autocomplete @codemirror/lint @codemirror/lang-json @codemirror/lang-javascript @codemirror/lang-python @codemirror/theme-one-dark">⚠ CM6 not installed — editing in plain mode</span>
        )}
        <span className="vsc-lang-badge">{LANG_META[language]?.label ?? language}</span>
      </div>

      {/* CM6 mount point */}
      <div
        className={`vsc-cm6-host${cmReady ? ' is-ready' : ''}`}
        ref={hostRef}
        style={{ height: editorHeight }}
      />

      {/* Plain textarea fallback — shown while CM6 loads or on error */}
      {!cmReady && (
        <textarea
          className="vsc-textarea-fallback"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ height: editorHeight }}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          wrap="off"
          aria-label="Code editor (loading…)"
          placeholder={cmError ? '// CodeMirror failed to load. Edit here.' : '// Loading editor…'}
        />
      )}

      {/* Resize handle */}
      <div
        className="vsc-resize-handle"
        onMouseDown={onResizeMouseDown}
        title="Drag to resize editor"
        role="separator"
        aria-label="Resize editor"
      />

      {/* Status bar */}
      <div className="vsc-statusbar">
        <span>Ln {cursorInfo.line}, Col {cursorInfo.col}</span>
        <span className="vsc-status-sep" />
        <span>{cursorInfo.lines} lines</span>
        <span className="vsc-status-sep" />
        <span>UTF-8</span>
        <span className="vsc-status-sep" />
        {language === 'json' && <><span>JSON Lint ✓</span><span className="vsc-status-sep" /></>}
        <span>Ctrl+Z undo · Ctrl+Space autocomplete</span>
      </div>
    </div>
  );
}

// ── Test sandbox panel ───────────────────────────────────────────
// ── Action type metadata for the trace UI ─────────────────────
const TRACE_META = {
  send_text: { icon: '💬', label: 'Text', color: '#00c88c' },
  send_buttons: { icon: '🔘', label: 'Buttons', color: '#007acc' },
  send_list: { icon: '📋', label: 'List', color: '#007acc' },
  send_flow: { icon: '⌥', label: 'Flow', color: '#9b59b6' },
  send_template: { icon: '📄', label: 'Template', color: '#e67e22' },
  send_reaction: { icon: '👍', label: 'Reaction', color: '#f39c12' },
  send_raw: { icon: '📦', label: 'Raw', color: '#7f8c8d' },
  set_storage: { icon: '🗄', label: 'Set Storage', color: '#1abc9c' },
  http_post: { icon: '🌐', label: 'HTTP POST', color: '#3498db' },
  http_post_form: { icon: '🌐', label: 'HTTP Form', color: '#3498db' },
  conversation_wait: { icon: '⏳', label: 'Conv Wait', color: '#e74c3c' },
  conversation_clear: { icon: '🧹', label: 'Conv Clear', color: '#95a5a6' },
  if_else: { icon: '🔀', label: 'If/Else', color: '#8e44ad' },
  for_each: { icon: '🔁', label: 'For Each', color: '#2980b9' },
  route_flow: { icon: '↪', label: 'Route Flow', color: '#16a085' },
  log: { icon: '📝', label: 'Log', color: '#95a5a6' },
  wait: { icon: '⏱', label: 'Wait', color: '#bdc3c7' },
  unknown: { icon: '•', label: 'Message', color: '#7f8c8d' },
};

function getTraceMeta(type) {
  return TRACE_META[type] || TRACE_META.unknown;
}

// ── Describe outbound payload in human-readable form ───────────
function describePayload(entry) {
  const p = entry.payload || {};
  const type = entry.type || 'unknown';

  if (type === 'send_text' || p.type === 'text') {
    return p.text?.body ?? p.text ?? '(empty)';
  }
  if (type === 'send_buttons') {
    const body = p.interactive?.body?.text ?? p.body ?? '';
    const btns = (p.interactive?.action?.buttons ?? [])
      .map(b => b.reply?.title ?? b.title ?? b.id).join(' · ');
    return body + (btns ? `\n[${btns}]` : '');
  }
  if (type === 'send_list') {
    const body = p.interactive?.body?.text ?? p.body ?? '';
    const rows = (p.interactive?.action?.sections ?? [])
      .flatMap(s => s.rows ?? [])
      .map(r => r.title)
      .slice(0, 5);
    return body + (rows.length ? `\n${rows.join(' · ')}${rows.length === 5 ? ' …' : ''}` : '');
  }
  if (type === 'send_template' || p.template) {
    return `Template: ${p.template?.name ?? '(unnamed)'}`;
  }
  if (type === 'send_reaction') {
    return `Emoji: ${p.reaction?.emoji ?? '?'} on msg ${p.reaction?.message_id ?? '?'}`;
  }
  if (type === 'send_flow') {
    return `Flow ID: ${p.interactive?.action?.parameters?.flow_id ?? '?'}`;
  }
  // Fallback: compact JSON
  const str = JSON.stringify(p);
  return str.length > 200 ? str.slice(0, 197) + '…' : str;
}

// ── Single trace step card ──────────────────────────────────────
function TraceStep({ entry, index, durationMs, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const meta = getTraceMeta(entry.type);
  const preview = describePayload(entry);
  const hasDetail = entry.payload && Object.keys(entry.payload).length > 0;

  return (
    <div className="ts-step">
      {/* Timeline spine */}
      <div className="ts-spine">
        <div className="ts-step-num" style={{ background: meta.color }}>{index + 1}</div>
        {!isLast && <div className="ts-spine-line" />}
      </div>

      {/* Card */}
      <div className={`ts-card${expanded ? ' ts-card-expanded' : ''}`}>
        <div className="ts-card-header" onClick={() => hasDetail && setExpanded(e => !e)}>
          <span className="ts-type-icon">{meta.icon}</span>
          <span className="ts-type-label" style={{ color: meta.color }}>{meta.label}</span>
          <span className="ts-preview">{preview.split('\n')[0]}</span>
          {hasDetail && (
            <span className="ts-expand-btn">{expanded ? '▲' : '▼'}</span>
          )}
        </div>
        {preview.includes('\n') && !expanded && (
          <div className="ts-preview-sub">{preview.split('\n').slice(1).join('\n')}</div>
        )}
        {expanded && (
          <div className="ts-payload">
            <pre className="ts-payload-pre">
              {JSON.stringify(entry.payload, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Chat bubble preview — "what does the user actually see" ──────
//  Renders the same transcript TraceStep consumes, but as real WhatsApp-
//  style bubbles in a phone mockup, with the simulated incoming message
//  shown first. Only actions that actually produce something the user
//  sees are rendered here (send_text, send_buttons, send_list,
//  send_template, send_flow, send_reaction) — everything else
//  (log, set_storage, http_post, if_else, for_each, wait, conversation_*)
//  is invisible to the end user and stays out of this view on purpose.
//  The Trace view (existing) is where that full technical picture lives.
const VISIBLE_ACTION_TYPES = new Set([
  'send_text', 'send_buttons', 'send_list', 'send_template', 'send_flow', 'send_reaction', 'send_raw',
]);

function BubbleContent({ entry }) {
  const p = entry.payload || {};
  const type = entry.type || 'unknown';

  if (type === 'send_text' || p.type === 'text') {
    const text = p.text?.body ?? p.text ?? '';
    return <div className="wa-bubble-text">{text}</div>;
  }

  if (type === 'send_buttons') {
    const body = p.interactive?.body?.text ?? p.body ?? '';
    const header = p.interactive?.header?.text;
    const footer = p.interactive?.footer?.text;
    const btns = p.interactive?.action?.buttons ?? [];
    return (
      <>
        {header && <div className="wa-bubble-header">{header}</div>}
        <div className="wa-bubble-text">{body}</div>
        {footer && <div className="wa-bubble-footer">{footer}</div>}
        <div className="wa-bubble-buttons">
          {btns.map((b, i) => (
            <div key={i} className="wa-bubble-btn">{b.reply?.title ?? b.title ?? b.id}</div>
          ))}
        </div>
      </>
    );
  }

  if (type === 'send_list') {
    const body = p.interactive?.body?.text ?? p.body ?? '';
    const buttonLabel = p.interactive?.action?.button ?? 'Choose';
    const sections = p.interactive?.action?.sections ?? [];
    return (
      <>
        <div className="wa-bubble-text">{body}</div>
        <div className="wa-bubble-buttons">
          <div className="wa-bubble-btn wa-bubble-btn-list">📋 {buttonLabel}</div>
        </div>
        <div className="wa-list-preview">
          {sections.flatMap(s => s.rows ?? []).slice(0, 4).map((r, i) => (
            <div key={i} className="wa-list-row">
              <div className="wa-list-row-title">{r.title}</div>
              {r.description && <div className="wa-list-row-desc">{r.description}</div>}
            </div>
          ))}
        </div>
      </>
    );
  }

  if (type === 'send_template' || p.template) {
    return <div className="wa-bubble-text wa-bubble-muted">📄 Template: <strong>{p.template?.name ?? '(unnamed)'}</strong></div>;
  }

  if (type === 'send_flow') {
    const header = p.interactive?.header?.text;
    const body = p.interactive?.body?.text ?? '';
    const cta = p.interactive?.action?.parameters?.flow_cta ?? 'Open';
    return (
      <>
        {header && <div className="wa-bubble-header">{header}</div>}
        <div className="wa-bubble-text">{body}</div>
        <div className="wa-bubble-buttons">
          <div className="wa-bubble-btn">📋 {cta}</div>
        </div>
      </>
    );
  }

  if (type === 'send_reaction') {
    return <div className="wa-reaction-note">reacted {p.reaction?.emoji ?? '👍'} to your message</div>;
  }

  // send_raw or anything else with a body/text-like field — best effort
  const fallbackText = p.text?.body ?? p.body ?? p.text ?? JSON.stringify(p).slice(0, 200);
  return <div className="wa-bubble-text wa-bubble-muted">{fallbackText}</div>;
}

function ChatBubblePreview({ incomingMessage, transcript }) {
  const visible = (transcript || []).filter(e => VISIBLE_ACTION_TYPES.has(e.type));
  const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="wa-phone">
      <div className="wa-phone-screen">
        {incomingMessage && (
          <div className="wa-row wa-row-in">
            <div className="wa-bubble wa-bubble-in">
              <div className="wa-bubble-text">{incomingMessage}</div>
              <div className="wa-bubble-time">{now}</div>
            </div>
          </div>
        )}

        {visible.length === 0 ? (
          <div className="wa-empty-note">
            This command didn't send anything the user would actually see — check the Trace tab to see what it did instead (e.g. saved data, called an API).
          </div>
        ) : (
          visible.map((entry, i) => (
            entry.type === 'send_reaction' ? (
              <div key={i} className="wa-row wa-row-system">
                <BubbleContent entry={entry} />
              </div>
            ) : (
              <div key={i} className="wa-row wa-row-out">
                <div className="wa-bubble wa-bubble-out">
                  <BubbleContent entry={entry} />
                  <div className="wa-bubble-time">{now} ✓✓</div>
                </div>
              </div>
            )
          ))
        )}
      </div>
    </div>
  );
}


function TestSandbox({ bId, cmd, language }) {
  const [testMsg, setTestMsg] = useState('');
  const [result, setResult] = useState(null);   // last full API response
  const [testing, setTesting] = useState(false);
  const [history, setHistory] = useState([]);     // run history
  const [activeRun, setActiveRun] = useState(0);      // index into history
  const [resultView, setResultView] = useState('chat'); // 'chat' | 'trace'
  const inputRef = useRef(null);

  // ── Guards ────────────────────────────────────────────────────
  if (language !== 'json') {
    return (
      <div className="ts-disabled">
        <span className="ts-disabled-icon">🧪</span>
        <div className="ts-disabled-title">JSON commands only</div>
        <div className="ts-disabled-text">
          Switch the language to <strong>JSON</strong> and save, then you can
          simulate incoming messages and see the full execution trace here.
        </div>
      </div>
    );
  }

  if (!cmd?.cId) {
    return (
      <div className="ts-disabled">
        <span className="ts-disabled-icon">💾</span>
        <div className="ts-disabled-title">Save first</div>
        <div className="ts-disabled-text">
          Create or save this command as a draft, then come back here to test it.
        </div>
      </div>
    );
  }

  // ── Run test ──────────────────────────────────────────────────
  async function runTest() {
    if (testing) return;
    setTesting(true);
    const msg = testMsg.trim() || cmd.cTrigger || '';
    try {
      const res = await testCommand({ bId, cId: cmd.cId, testMessage: msg });
      const data = res.data ?? {};
      const run = {
        id: Date.now(),
        message: msg || '(empty)',
        success: data.success ?? false,
        error: data.error ?? null,
        transcript: data.transcript ?? [],
        duration_ms: data.duration_ms ?? null,
        matched: data.matched_groups ?? [],
        ts: new Date().toLocaleTimeString(),
      };
      setHistory(prev => [run, ...prev].slice(0, 10)); // keep last 10
      setActiveRun(0);
      setResult(run);
    } catch (e) {
      const run = {
        id: Date.now(), message: msg || '(empty)', success: false,
        error: e?.response?.data?.message ?? 'Network error',
        transcript: [], duration_ms: null, matched: [],
        ts: new Date().toLocaleTimeString(),
      };
      setHistory(prev => [run, ...prev].slice(0, 10));
      setActiveRun(0);
      setResult(run);
    }
    setTesting(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  const displayed = history[activeRun] ?? result;

  return (
    <div className="ts-root">
      {/* ── Input bar ───────────────────────────────────── */}
      <div className="ts-input-bar">
        <div className="ts-input-wrap">
          <span className="ts-input-icon">💬</span>
          <input
            ref={inputRef}
            className="ts-input"
            placeholder={`Simulate a message… (default: "${cmd.cTrigger || '*'}")`}
            value={testMsg}
            onChange={e => setTestMsg(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runTest()}
          />
        </div>
        <button
          className={`ts-run-btn${testing ? ' ts-run-btn-loading' : ''}`}
          onClick={runTest}
          disabled={testing}
        >
          {testing
            ? <><span className="spinner spinner-sm" /> Running…</>
            : <><PlayIcon /> Run test</>}
        </button>
      </div>

      {/* ── Hint ────────────────────────────────────────── */}
      {!displayed && (
        <div className="ts-hint">
          <div className="ts-hint-icon">🧪</div>
          <div className="ts-hint-title">Webhook simulator</div>
          <div className="ts-hint-text">
            Type a message above and press <kbd>Enter</kbd> or <strong>Run test</strong>.
            CommandKit will run your action array through the real engine using
            a mock sender — no messages are actually sent to WhatsApp.
          </div>
          <div className="ts-hint-chips">
            {['Hello', '/start', cmd.cTrigger].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).map(s => (
              <button key={s} className="ts-hint-chip" onClick={() => { setTestMsg(s); setTimeout(runTest, 50); }}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Result area ─────────────────────────────────── */}
      {displayed && (
        <div className="ts-result">
          {/* Run history tabs */}
          {history.length > 1 && (
            <div className="ts-history-tabs">
              {history.map((run, i) => (
                <button
                  key={run.id}
                  className={`ts-history-tab${i === activeRun ? ' is-active' : ''}${!run.success ? ' is-error' : ''}`}
                  onClick={() => { setActiveRun(i); setResult(run); }}
                  title={run.message}
                >
                  <span className={`ts-htab-dot${run.success ? ' ts-htab-ok' : ' ts-htab-err'}`} />
                  {run.ts}
                </button>
              ))}
            </div>
          )}

          {/* Status bar */}
          <div className={`ts-status-bar${displayed.success ? ' ts-status-ok' : ' ts-status-err'}`}>
            <span className="ts-status-icon">{displayed.success ? '✓' : '✕'}</span>
            <span className="ts-status-msg">
              "{displayed.message}"
              {displayed.success
                ? ` → ${displayed.transcript.length} action${displayed.transcript.length !== 1 ? 's' : ''} executed`
                : ` → ${displayed.error}`}
            </span>
            {displayed.duration_ms !== null && (
              <span className="ts-status-dur">{displayed.duration_ms}ms</span>
            )}
          </div>

          {/* Regex capture groups */}
          {displayed.matched?.length > 0 && (
            <div className="ts-groups">
              <span className="ts-groups-label">Regex captures:</span>
              {displayed.matched.map((g, i) => (
                <span key={i} className="ts-group-chip">
                  <span className="ts-group-key">{'{{match.' + (i + 1) + '}}'}</span>
                  <span className="ts-group-val">{g}</span>
                </span>
              ))}
            </div>
          )}

          {/* View toggle */}
          {displayed.success && (
            <div className="ts-view-toggle">
              <button
                className={`ts-view-toggle-btn${resultView === 'chat' ? ' is-active' : ''}`}
                onClick={() => setResultView('chat')}
              >
                💬 Chat Preview
              </button>
              <button
                className={`ts-view-toggle-btn${resultView === 'trace' ? ' is-active' : ''}`}
                onClick={() => setResultView('trace')}
              >
                🧾 Trace
              </button>
            </div>
          )}

          {/* Chat preview */}
          {displayed.success && resultView === 'chat' && (
            <ChatBubblePreview incomingMessage={displayed.message} transcript={displayed.transcript} />
          )}

          {/* Trace */}
          {(!displayed.success || resultView === 'trace') && (
            displayed.transcript.length > 0 ? (
              <div className="ts-trace">
                {displayed.transcript.map((entry, i) => (
                  <TraceStep
                    key={i}
                    entry={entry}
                    index={i}
                    isLast={i === displayed.transcript.length - 1}
                  />
                ))}
              </div>
            ) : displayed.success ? (
              <div className="ts-empty-trace">
                <span>⚡</span>
                <span>Command ran successfully but sent no outbound messages.</span>
              </div>
            ) : null
          )}
        </div>
      )}
    </div>
  );
}

// ── Editor modal ──────────────────────────────────────────────
function CommandModal({ bId, cmd, defaultLanguage, onClose, onSaved }) {
  const isEdit = !!cmd;

  // For json-language commands the editor shows cActions (not cCode).
  // cCode is always empty for those — the actions array is the source of truth.
  function resolveEditorValue(command) {
    if (!command) return DEFAULT_CODE['json'];
    if (command.cLanguage === 'json') {
      const raw = command.cActions;
      if (!raw) return '[]';
      if (typeof raw === 'string') {
        try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
      }
      return JSON.stringify(raw, null, 2);
    }
    return command.cCode ?? DEFAULT_CODE[command.cLanguage] ?? '';
  }

  const VALID_CMD_LANGUAGES = ['json', 'javascript', 'python'];
  const botDefaultLanguage = VALID_CMD_LANGUAGES.includes(defaultLanguage) ? defaultLanguage : 'json';

  const [form, setForm] = useState({
    cTrigger: cmd?.cTrigger ?? '',
    cTriggerType: cmd?.cTriggerType ?? 'exact',
    cType: cmd?.cType ?? 'command',
    cLanguage: cmd?.cLanguage ?? botDefaultLanguage,
    cCode: cmd ? resolveEditorValue(cmd) : (DEFAULT_CODE[botDefaultLanguage] ?? DEFAULT_CODE['json']),
    cIsActive: cmd?.cIsActive ?? 1,
  });
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [activeSection, setActiveSection] = useState('edit'); // 'edit' | 'test'

  function set(k, v) {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === 'cLanguage' && !isEdit) next.cCode = DEFAULT_CODE[v] ?? '';

      // Keep cType, cTriggerType, and cTrigger in sync for wildcard.
      // The DB matcher (matchLiveCommand) uses cTriggerType — so if either
      // the Type buttons or the Match Type dropdown is set to wildcard,
      // both fields must agree and the trigger must be '*'.
      if (k === 'cType' && v === 'wildcard') {
        next.cTriggerType = 'wildcard';
        next.cTrigger = '*';
      }
      if (k === 'cType' && v !== 'wildcard' && f.cTriggerType === 'wildcard') {
        // User switched away from wildcard type — reset match type to exact
        next.cTriggerType = 'exact';
        next.cTrigger = '';
      }
      if (k === 'cTriggerType' && v === 'wildcard') {
        next.cType = 'wildcard';
        next.cTrigger = '*';
      }
      if (k === 'cTriggerType' && v !== 'wildcard' && f.cType === 'wildcard') {
        // User changed match type away from wildcard — reset cType to command
        next.cType = 'command';
        next.cTrigger = '';
      }

      return next;
    });
  }

  function regexPreviewError() {
    if (form.cTriggerType !== 'regex' || !form.cTrigger) return null;
    try { new RegExp(form.cTrigger); return null; }
    catch (e) { return e.message; }
  }

  function requestSave() {
    if (form.cTriggerType !== 'wildcard' && !form.cTrigger.trim()) {
      return toast.error('Trigger is required');
    }
    const regexErr = regexPreviewError();
    if (regexErr) return toast.error(`Invalid regex: ${regexErr}`);
    setConfirming(true);
  }

  async function confirmSave() {
    setSaving(true);
    try {
      // For json language: send editor content as cActions, keep cCode empty.
      // Always normalize cTrigger to '*' for wildcard commands — the backend
      // matchLiveCommand() checks for trigger === '*' || trigger === ''.
      const normalizedForm = {
        ...form,
        cTrigger: form.cTriggerType === 'wildcard' ? '*' : form.cTrigger,
      };
      const payload = normalizedForm.cLanguage === 'json'
        ? { bId, ...normalizedForm, cActions: normalizedForm.cCode, cCode: '' }
        : { bId, ...normalizedForm };
      const res = isEdit
        ? await updateCommand({ ...payload, cId: cmd.cId })
        : await createCommand(payload);
      if (res.data?.success) {
        toast.success(isEdit ? 'Command saved as draft' : 'Command created as draft');
        onSaved();
      } else {
        toast.error(res.data?.message ?? 'Failed to save command');
        setConfirming(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message ?? 'Network error — couldn\u2019t save');
      setConfirming(false);
    }
    setSaving(false);
  }

  const meta = TYPE_META[form.cType];
  const triggerTypeMeta = TRIGGER_TYPE_META[form.cTriggerType];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-group">
            <span className="modal-title-icon">{meta?.icon ?? '⌘'}</span>
            <div>
              <div className="modal-title">{isEdit ? 'Edit command' : 'New command'}</div>
              <div className="modal-subtitle">{isEdit ? `Editing "${cmd.cTrigger}"` : 'Define a trigger and its behavior'}</div>
            </div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>

        <div className="modal-tabs">
          <button
            className={`modal-tab-btn ${activeSection === 'edit' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('edit')}
          >
            Define
          </button>
          <button
            className={`modal-tab-btn ${activeSection === 'test' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('test')}
          >
            Test
          </button>
        </div>

        {activeSection === 'edit' ? (
          <div className="modal-body">
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Trigger</label>
                <input
                  className="form-input"
                  placeholder="/start  or  Menu  or  order #(\d+)"
                  value={form.cTrigger}
                  onChange={(e) => set('cTrigger', e.target.value)}
                  disabled={form.cTriggerType === 'wildcard'}
                />
                <div className="form-hint">What the user types or sends to fire this command.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Match type</label>
                <select
                  className="form-input"
                  value={form.cTriggerType}
                  onChange={(e) => set('cTriggerType', e.target.value)}
                >
                  {TRIGGER_TYPES.map((t) => (
                    <option key={t} value={t}>{TRIGGER_TYPE_META[t].label}</option>
                  ))}
                </select>
                <div className="form-hint">{triggerTypeMeta.hint}</div>
                {regexPreviewError() && (
                  <div className="form-error">Invalid regex: {regexPreviewError()}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Type</label>
              <div className="segmented">
                {TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`segmented-btn ${form.cType === t ? 'is-selected' : ''}`}
                    onClick={() => set('cType', t)}
                  >
                    <span className="segmented-icon">{TYPE_META[t].icon}</span>
                    {TYPE_META[t].label}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Language</label>
                <select className="form-input" value={form.cLanguage} onChange={(e) => set('cLanguage', e.target.value)}>
                  {LANGUAGES.map((l) => <option key={l} value={l}>{LANG_META[l].label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <button
                  type="button"
                  className={`toggle-field ${form.cIsActive ? 'is-on' : ''}`}
                  onClick={() => set('cIsActive', form.cIsActive ? 0 : 1)}
                >
                  <span className="toggle-track"><span className="toggle-thumb" /></span>
                  {form.cIsActive ? 'Active — runs when live' : 'Inactive — saved but won\u2019t run even if live'}
                </button>
              </div>
            </div>

            {isEdit && cmd?.cStatus === 'live' && (
              <div className="live-edit-warning">
                <WarnIcon /> Saving will move this command back to <strong>Draft</strong>. Publish again when you're ready to go live.
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Actions</label>
              <CodeEditor
                value={form.cCode}
                onChange={(v) => set('cCode', v)}
                language={form.cLanguage}
              />
            </div>
          </div>
        ) : (
          <div className="modal-body">
            <TestSandbox bId={bId} cmd={cmd} language={form.cLanguage} />
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          {activeSection === 'edit' && (
            <button className="btn btn-primary" onClick={requestSave}>
              {isEdit ? 'Save changes' : 'Create command'}
            </button>
          )}
        </div>
      </div>

      {confirming && (
        <div className="modal-backdrop modal-backdrop-stacked" onClick={() => !saving && setConfirming(false)}>
          <div className="modal modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon"><WarnIcon /></div>
            <div className="confirm-title">{isEdit ? 'Save these changes?' : 'Create this command?'}</div>
            <div className="confirm-body">
              {isEdit
                ? <>This saves as a <strong>draft</strong>. {cmd.cStatus === 'live' && 'The currently live version keeps running until you publish this update.'}</>
                : <>This will create <strong>{form.cTrigger || '*'}</strong> as a draft {TYPE_META[form.cType].label.toLowerCase()}. Publish it afterward to go live.</>}
            </div>
            <div className="confirm-footer">
              <button className="btn btn-outline" onClick={() => setConfirming(false)} disabled={saving}>Keep editing</button>
              <button className="btn btn-primary" onClick={confirmSave} disabled={saving}>
                {saving ? <span className="spinner" /> : (isEdit ? 'Yes, save' : 'Yes, create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────
export default function CommandsTab({ bId: bIdProp, bot }) {
  const bId = bIdProp ?? bot?.bId;
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | cmd object
  const [previewTarget, setPreviewTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [query, setQuery] = useState('');
  const [publishingId, setPublishingId] = useState(null);
  const [togglingActiveId, setTogglingActiveId] = useState(null);

  async function load() {
    try {
      const res = await listCommands(bId);
      if (res.data?.success) setCommands(res.data.commands ?? []);
    } catch { toast.error('Failed to load commands'); }
    setLoading(false);
  }

  useEffect(() => { load(); }, [bId]);

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteCommand(bId, deleteTarget.cId);
      toast.success('Command deleted');
      setCommands((prev) => prev.filter((c) => c.cId !== deleteTarget.cId));
      setDeleteTarget(null);
    } catch { toast.error('Failed to delete command'); }
    setDeleting(false);
  }

  async function handlePublish(cmd) {
    setPublishingId(cmd.cId);
    try {
      const res = await publishCommand(bId, cmd.cId);
      if (res.data?.success) {
        toast.success(`"${cmd.cTrigger || '*'}" is now live`);
        setCommands((prev) => prev.map((c) => c.cId === cmd.cId ? { ...c, cStatus: 'live' } : c));
      } else {
        toast.error(res.data?.message ?? 'Failed to publish');
      }
    } catch { toast.error('Failed to publish command'); }
    setPublishingId(null);
  }

  async function handleUnpublish(cmd) {
    setPublishingId(cmd.cId);
    try {
      const res = await unpublishCommand(bId, cmd.cId);
      if (res.data?.success) {
        toast.success(`"${cmd.cTrigger || '*'}" moved back to draft`);
        setCommands((prev) => prev.map((c) => c.cId === cmd.cId ? { ...c, cStatus: 'draft' } : c));
      } else {
        toast.error(res.data?.message ?? 'Failed to unpublish');
      }
    } catch { toast.error('Failed to unpublish command'); }
    setPublishingId(null);
  }

  async function handleToggleActive(cmd, active) {
    setTogglingActiveId(cmd.cId);
    try {
      const res = await toggleCommandActive(bId, cmd.cId, active);
      if (res.data?.success) {
        toast.success(`"${cmd.cTrigger || '*'}" ${active ? 'activated' : 'deactivated'}`);
        setCommands((prev) => prev.map((c) => c.cId === cmd.cId ? { ...c, cIsActive: active ? 1 : 0 } : c));
      } else {
        toast.error(res.data?.message ?? 'Failed to update');
      }
    } catch { toast.error('Failed to update command'); }
    setTogglingActiveId(null);
  }

  const filtered = commands.filter((c) =>
    !query.trim() || (c.cTrigger || '').toLowerCase().includes(query.trim().toLowerCase())
  );

  if (loading) {
    return (
      <div className="tab-loading">
        <span className="spinner spinner-dark" /> Loading commands…
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="tab-toolbar">
        <div>
          <div className="tab-title">Commands</div>
          <div className="tab-sub">{commands.length} command{commands.length !== 1 ? 's' : ''} configured</div>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}>
          <span className="btn-plus">+</span> New command
        </button>
      </div>

      {commands.length > 0 && (
        <div className="search-bar">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="search-icon">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
            <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            className="search-input"
            placeholder="Search by trigger…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}

      {commands.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">⌘</div>
          <div className="tab-empty-title">No commands yet</div>
          <div className="tab-empty-text">Commands are how your bot listens and reacts — add one to get started.</div>
          <button className="link-btn" onClick={() => setModal('new')}>Create your first command →</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">∅</div>
          <div className="tab-empty-title">No matches for "{query}"</div>
          <div className="tab-empty-text">Try a different search term.</div>
        </div>
      ) : (
        <div className="cmd-list">
          {filtered.map((cmd) => (
            <CommandRow
              key={cmd.cId}
              cmd={cmd}
              onEdit={(c) => setModal(c)}
              onPreview={(c) => setPreviewTarget(c)}
              onDelete={(c) => setDeleteTarget(c)}
              onPublish={handlePublish}
              onUnpublish={handleUnpublish}
              onToggleActive={handleToggleActive}
              publishing={publishingId === cmd.cId}
              togglingActive={togglingActiveId === cmd.cId}
            />
          ))}
        </div>
      )}

      {modal && (
        <CommandModal
          bId={bId}
          cmd={modal === 'new' ? null : modal}
          defaultLanguage={bot?.bLanguage}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}

      {previewTarget && (
        <QuickPreviewModal
          bId={bId}
          cmd={previewTarget}
          onClose={() => setPreviewTarget(null)}
        />
      )}

      {deleteTarget && (
        <div className="modal-backdrop" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="modal modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon confirm-icon-danger"><TrashIcon /></div>
            <div className="confirm-title">Delete this command?</div>
            <div className="confirm-body">
              <strong>{deleteTarget.cTrigger || '*'}</strong> will be permanently removed and stop responding right away. This can't be undone.
            </div>
            <div className="confirm-footer">
              <button className="btn btn-outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? <span className="spinner" /> : 'Delete command'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}